import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BadgeCheck, CalendarClock, CheckCircle2, Clock, Copy, MessageCircle, RefreshCw, ShieldAlert, Trash2, UserPlus, XCircle,
} from "lucide-react";
import { api, dt, copyText, errMsg, openWa, rp, waLink, waNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Stat = ({ label, value, icon: Icon, tone = "text-cyan-400", testid }) => (
  <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${tone}`} />
    </div>
    <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
  </div>
);

const KindBadge = ({ kind }) => (
  <span className={`rounded-full border px-2 py-0.5 text-[10px] ${kind === "renewal" ? "border-cyan-500/40 text-cyan-300" : "border-violet-500/40 text-violet-300"}`}>
    {kind === "renewal" ? "Perpanjang" : "Baru"}
  </span>
);

const StatusPill = ({ status }) => {
  const m = {
    pending: "border-amber-500/40 text-amber-300",
    approved: "border-emerald-500/40 text-emerald-300",
    rejected: "border-red-500/40 text-red-300",
    active: "border-emerald-500/40 text-emerald-300",
    expired: "border-red-500/40 text-red-300",
    unlimited: "border-cyan-500/40 text-cyan-300",
  };
  const label = { unlimited: "tanpa batas", expired: "berakhir", active: "aktif" }[status] || status;
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] ${m[status] || "border-border"}`}>{label}</span>;
};

export default function Members() {
  const [tab, setTab] = useState("pending");
  const [regs, setRegs] = useState([]);
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState(null); // {id, code, username}
  const [reason, setReason] = useState("");
  const [renew, setRenew] = useState(null); // member
  const [renewPlan, setRenewPlan] = useState("");
  const [notif, setNotif] = useState(null); // {phone, message, title}

  const load = useCallback(async () => {
    try {
      const [r, m, s, p] = await Promise.all([
        api.get("/registrations?status=all"),
        api.get("/members"),
        api.get("/registrations/summary"),
        api.get("/public/plans"),
      ]);
      setRegs(r.data || []);
      setMembers(m.data || []);
      setSummary(s.data || null);
      setPlans(p.data?.plans || []);
      if (!renewPlan && p.data?.plans?.length) setRenewPlan(p.data.plans[0].key);
    } catch (e) { toast.error(errMsg(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = regs.filter((r) => r.status === "pending");
  const history = regs.filter((r) => r.status !== "pending");

  const approve = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${r.id}/approve`);
      toast.success(`${r.kind === "renewal" ? "Perpanjangan" : "Pendaftaran"} ${r.code} disetujui`);
      setNotif({ phone: data.phone, message: data.wa_message, title: "Kirim notifikasi akun aktif" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doReject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reject.id}/reject`, { reason });
      toast.success(`Pendaftaran ${reject.code} ditolak`);
      setNotif({ phone: data.phone, message: data.wa_message, title: "Kirim pemberitahuan penolakan" });
      setReject(null); setReason("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const hapus = async (r) => {
    if (!window.confirm(`Hapus data pendaftaran ${r.code}?`)) return;
    try {
      await api.delete(`/registrations/${r.id}`);
      toast.success("Data pendaftaran dihapus");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doRenew = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/members/${renew.id}/renew`, { plan: renewPlan });
      toast.success(`Langganan ${renew.username} diperpanjang sampai ${dt(data.expires_at)}`);
      setNotif({ phone: renew.phone, message: data.wa_message, title: "Kirim notifikasi perpanjangan" });
      setRenew(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  /** Ambil ulang pesan WA dari server lalu buka dialog kirim (bisa diulang kapan saja). */
  const kirimWa = async (kind, id) => {
    try {
      const { data } = await api.get(`/${kind === "member" ? "members" : "registrations"}/${id}/wa`);
      setNotif({ phone: data.phone, message: data.message, title: data.title || "Kirim notifikasi" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="fade-up space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Pendaftaran Baru" value={summary?.pending ?? "-"} icon={Clock} tone="text-amber-400" testid="stat-pending" />
        <Stat label="Member Berlangganan" value={summary?.members ?? "-"} icon={BadgeCheck} tone="text-emerald-400" testid="stat-members" />
        <Stat label="Segera Habis (≤7 hari)" value={summary?.expiring_soon ?? "-"} icon={CalendarClock} tone="text-cyan-400" testid="stat-soon" />
        <Stat label="Sudah Berakhir" value={summary?.expired ?? "-"} icon={ShieldAlert} tone="text-red-400" testid="stat-expired" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <TabsList className="flex flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1">
          <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-lg text-xs">Pendaftaran Masuk ({pending.length})</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="rounded-lg text-xs">Member ({members.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="rounded-lg text-xs">Riwayat ({history.length})</TabsTrigger>
        </TabsList>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="members-refresh">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Muat ulang
        </Button>
      </div>

      <TabsContent value="pending">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="pending-table">
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Usaha / Nama</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Nominal</TableHead>
                <TableHead>Masuk</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((r) => (
                <TableRow key={r.id} data-testid={`pending-row-${r.code}`}>
                  <TableCell data-label="Kode" className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell data-label="Jenis"><KindBadge kind={r.kind} /></TableCell>
                  <TableCell data-label="Usaha / Nama">
                    <p className="font-medium">{r.business_name || "-"}</p>
                    <p className="text-xs text-muted-foreground">{r.name}</p>
                  </TableCell>
                  <TableCell data-label="Username" className="font-mono text-xs">{r.username}</TableCell>
                  <TableCell data-label="WhatsApp" className="font-mono text-xs">{r.phone}</TableCell>
                  <TableCell data-label="Paket">{r.plan_label}</TableCell>
                  <TableCell data-label="Nominal">{rp(r.price)}</TableCell>
                  <TableCell data-label="Masuk" className="text-xs text-muted-foreground">{dt(r.created_at)}</TableCell>
                  <TableCell data-label="" className="cell-actions space-x-1 text-right">
                    <Button size="sm" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" disabled={busy} onClick={() => approve(r)} data-testid={`approve-${r.code}`}>
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Setujui
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setReject(r); setReason(""); }} data-testid={`reject-${r.code}`}>
                      <XCircle className="mr-1 h-3.5 w-3.5" /> Tolak
                    </Button>
                    <Button asChild size="icon" variant="ghost" className="h-8 w-8" title="Chat pendaftar">
                      <a href={waLink(r.phone, `Halo ${r.name}, terkait pendaftaran ${r.code} di ALPHA BILL.`)} target="_blank" rel="noreferrer">
                        <MessageCircle className="h-4 w-4 text-emerald-400" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!pending.length && (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">Belum ada pendaftaran masuk.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="members">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="members-table">
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead>Usaha / Nama</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Berlaku Sampai</TableHead>
                <TableHead>Sisa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} data-testid={`member-row-${m.username}`}>
                  <TableCell data-label="Username" className="font-mono text-xs">{m.username}</TableCell>
                  <TableCell data-label="Usaha / Nama">
                    <p className="font-medium">{m.business_name || m.name || "-"}</p>
                    {m.registration_code ? <p className="font-mono text-[10px] text-muted-foreground">{m.registration_code}</p> : null}
                  </TableCell>
                  <TableCell data-label="WhatsApp" className="font-mono text-xs">{m.phone || "-"}</TableCell>
                  <TableCell data-label="Paket">{m.plan_label || (m.managed ? m.plan : "—")}</TableCell>
                  <TableCell data-label="Berlaku Sampai" className="text-xs">{m.expires_at ? dt(m.expires_at) : "—"}</TableCell>
                  <TableCell data-label="Sisa">
                    {m.managed ? (
                      <b className={m.expired ? "text-red-400" : m.days_left <= 7 ? "text-amber-400" : "text-emerald-400"}>
                        {m.expired ? "0 hari" : `${m.days_left} hari`}
                      </b>
                    ) : "—"}
                  </TableCell>
                  <TableCell data-label="Status"><StatusPill status={m.status} /></TableCell>
                  <TableCell data-label="" className="cell-actions space-x-1 text-right">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setRenew(m); setRenewPlan(m.plan || plans[0]?.key || ""); }} data-testid={`renew-${m.username}`}>
                      <CalendarClock className="mr-1 h-3.5 w-3.5" /> Perpanjang
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full border-emerald-500/40 text-emerald-300" onClick={() => kirimWa("member", m.id)} data-testid={`wa-member-${m.username}`}>
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> Kirim WA
                    </Button>
                    {m.phone ? (
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Chat member"
                        onClick={() => openWa(m.phone, `Halo ${m.name || m.username}, informasi langganan ALPHA BILL Anda.`)}>
                        <MessageCircle className="h-4 w-4 text-emerald-400" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!members.length && (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">Belum ada akun admin.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="history">
        <div className="mobile-cards overflow-x-auto rounded-2xl border border-border bg-card scrollbar-thin">
          <Table data-testid="history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Paket</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Diproses</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((r) => (
                <TableRow key={r.id} data-testid={`history-row-${r.code}`}>
                  <TableCell data-label="Kode" className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell data-label="Jenis"><KindBadge kind={r.kind} /></TableCell>
                  <TableCell data-label="Username" className="font-mono text-xs">{r.username}</TableCell>
                  <TableCell data-label="Paket">{r.plan_label} · {rp(r.price)}</TableCell>
                  <TableCell data-label="Status">
                    <StatusPill status={r.status} />
                    {r.reject_reason ? <p className="text-[10px] text-muted-foreground">{r.reject_reason}</p> : null}
                  </TableCell>
                  <TableCell data-label="Diproses" className="text-xs text-muted-foreground">{dt(r.approved_at || r.rejected_at || r.created_at)}</TableCell>
                  <TableCell data-label="" className="cell-actions space-x-1 text-right">
                    <Button size="sm" variant="outline" className="rounded-full border-emerald-500/40 text-emerald-300" onClick={() => kirimWa("reg", r.id)} data-testid={`wa-reg-${r.code}`}>
                      <MessageCircle className="mr-1 h-3.5 w-3.5" /> Kirim WA
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => hapus(r)} data-testid={`delete-reg-${r.code}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!history.length && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">Belum ada riwayat.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      {/* dialog tolak */}
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Tolak {reject?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Pendaftar {reject?.username} akan diberi tahu lewat WhatsApp.</p>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Alasan</Label>
              <Input className="h-11 rounded-xl bg-secondary/50" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="pembayaran belum diterima" data-testid="reject-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setReject(null)}>Batal</Button>
            <Button className="rounded-full bg-red-600 text-white hover:bg-red-700" disabled={busy} onClick={doReject} data-testid="reject-submit">Tolak Pendaftaran</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* dialog perpanjang */}
      <Dialog open={!!renew} onOpenChange={(o) => !o && setRenew(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Perpanjang {renew?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Masa aktif ditambahkan dari {renew?.expires_at && !renew?.expired ? `tanggal berakhir saat ini (${dt(renew.expires_at)})` : "hari ini"}.
            </p>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paket</Label>
              <select
                className="h-11 w-full rounded-xl border border-input bg-secondary/50 px-3 text-sm"
                value={renewPlan} onChange={(e) => setRenewPlan(e.target.value)} data-testid="renew-plan"
              >
                {plans.map((p) => <option key={p.key} value={p.key}>{p.label} · {rp(p.price)} · {p.months} bulan</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setRenew(null)}>Batal</Button>
            <Button className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white" disabled={busy} onClick={doRenew} data-testid="renew-submit">
              <UserPlus className="mr-1.5 h-4 w-4" /> Perpanjang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* dialog kirim notifikasi WA */}
      <Dialog open={!!notif} onOpenChange={(o) => !o && setNotif(null)}>
        <DialogContent
          className="sm:max-w-md"
          // jangan tutup saat fokus pindah ke tab/aplikasi WhatsApp — dialog hanya
          // ditutup lewat tombol "Tutup" supaya pesan bisa dicoba/disalin ulang.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <DialogHeader><DialogTitle className="font-display">{notif?.title || "Kirim notifikasi"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {notif?.phone ? (
              <p className="text-sm text-muted-foreground">
                Tujuan: <span className="font-mono text-foreground">+{waNumber(notif.phone)}</span>. Pesan akan dibuka di
                WhatsApp, tinggal tekan kirim.
              </p>
            ) : (
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-200" data-testid="notif-no-phone">
                Nomor WhatsApp member ini kosong. Salin pesannya lalu kirim manual.
              </p>
            )}
            <pre className="max-h-52 overflow-auto whitespace-pre-wrap rounded-xl bg-secondary/50 p-3 text-xs" data-testid="notif-message">{notif?.message}</pre>
            <p className="text-[11px] text-muted-foreground">
              Kalau WhatsApp tidak terbuka (mis. aplikasi terpasang di HP memblokir tab baru), tekan
              &quot;Salin Pesan&quot; lalu tempel di chat WhatsApp member.
            </p>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="ghost" className="rounded-full" onClick={() => setNotif(null)} data-testid="notif-close">Tutup</Button>
            <Button variant="outline" className="rounded-full" data-testid="notif-copy"
              onClick={async () => {
                const ok = await copyText(notif?.message || "");
                ok ? toast.success("Pesan disalin") : toast.error("Gagal menyalin, silakan blok teksnya lalu copy manual");
              }}>
              <Copy className="mr-1.5 h-4 w-4" /> Salin Pesan
            </Button>
            <Button
              className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="notif-wa-send"
              disabled={!notif?.phone}
              onClick={() => {
                const ok = openWa(notif?.phone, notif?.message || "");
                if (!ok) toast.error("Nomor WhatsApp tidak valid");
              }}
            >
              <MessageCircle className="mr-1.5 h-4 w-4" /> Buka WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
